import { useMemo } from 'react';
import { ConditionSingleValueSelectProps } from 'ui/queryBuilder/types';
import { ConditionTriggerButton } from 'ui/queryBuilder';
import { EllipsizedContent } from 'ui/queryBuilder/ConditionInput';
import { SelectValueType } from 'ui/selects';
import { isObject } from 'utils/helpers';
import { useCatalogValueLabel } from './hooks';
import { isCatalogQueryValue } from './utils';
import {
  CatalogQueryContextProvider,
  useCatalogQueryContext,
} from '../../../contexts/catalogQuery';
import { CatalogQueryDrawer } from '../../catalogQuery';
import { CatalogData, CatalogQueryDetails } from '../../../types';

export const CatInSegComponent = <TValue extends SelectValueType | CatalogData>({
  isDisabled,
  isReadOnly,
  value,
  field,
  fieldType,
  dataType,
  ...props
}: Omit<ConditionSingleValueSelectProps<TValue>, 'placeholder'>) => {
  const catalogQueryValue = useMemo<CatalogQueryDetails>(
    () => ({
      searchField: field,
      fieldType,
      dataType,
      ...(isObject(value) && value),
    }),
    [field, fieldType, dataType, value],
  );
  const { onOpen } = useCatalogQueryContext();
  const catalogValueLabel = useCatalogValueLabel(value);
  const isEmpty = useMemo(() => !isCatalogQueryValue(value), [value]);

  return (
    <>
      <ConditionTriggerButton
        {...props}
        isEmpty={isEmpty}
        isDisabled={isDisabled}
        isReadOnly={isReadOnly}
        onClick={() => onOpen(catalogQueryValue)}
      >
        <EllipsizedContent>{catalogValueLabel}</EllipsizedContent>
      </ConditionTriggerButton>
    </>
  );
};

export type CatInSegInputProps = Omit<
  ConditionSingleValueSelectProps<SelectValueType | CatalogData>,
  'placeholder'
>;

export const CatInSegInput = ({ value, onChange, ...props }: CatInSegInputProps) => (
  <CatalogQueryContextProvider
    onChange={(val) => {
      if (val) onChange(val as SelectValueType | CatalogData);
    }}
  >
    <CatInSegComponent
      value={value}
      onChange={(val) => onChange(val as SelectValueType | CatalogData)}
      {...props}
    />
    <CatalogQueryDrawer />
  </CatalogQueryContextProvider>
);
